import { ModerationInfo } from "@circle-react/components/ModerationInfo";
import { shouldRenderContent } from "@circle-react/components/ModerationInfo/components/shared";
import { useCurrentPostContext } from "@circle-react/contexts/currentPostContext";
import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { isImagePost } from "@circle-react/helpers/postHelpers";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { Badge } from "@circleco/compass/components/Badge";

import { t } from "@/i18n-js/instance";
import { canManageSpace } from "@/react/helpers/spaceHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useRouteHelpers } from "@/react/routes/Main/useRouteHelpers";
import { POST_TYPE } from "@/react/types/FlaggedContent";

import { PostActions } from "./PostActions";
import { PostMetaInfo } from "./PostMetaInfo";
import { PostName } from "./PostName";

export interface PostHeaderProps {
  hideAuthorHeadline?: boolean;
  hideBookmarkButton?: boolean;
  hidePinnedToTopIcon?: boolean;
  hideShareLink?: boolean;
  hideSummarizeButton?: boolean;
  isRenderedOutsideTheSpace?: boolean;
  metaInfoWrapperClassName?: string;
  onChange: (data: any) => void;
  onPostDestroy: (data: any) => void;
  post: any;
  refetchPosts?: () => void;
  tagsLimit: number;
  tagsShowMoreLabelVariant?: "words" | "numbers";
}

export const PostHeader = ({
  hideAuthorHeadline = false,
  hideBookmarkButton = false,
  hideSummarizeButton = false,
  hidePinnedToTopIcon = false,
  hideShareLink = false,
  isRenderedOutsideTheSpace = false,
  metaInfoWrapperClassName,
  onChange,
  onPostDestroy,
  post,
  refetchPosts,
  tagsLimit,
  tagsShowMoreLabelVariant,
}: PostHeaderProps) => {
  const { data: space } = useCurrentSpaceContext();
  const { usedIn, isUsedInMinimalTipTapViewModal } = useCurrentPostContext();
  const doesPostHaveCoverImage = post?.cover_image_url;
  const isSmScreen = useSmScreenMediaQuery();
  const { isFeedRoute } = useRouteHelpers();

  const shouldRenderMetaFirstAndThenTitle =
    isRenderedOutsideTheSpace ||
    isFeedRoute ||
    isSmScreen ||
    usedIn === "minimal-post-modal";

  return (
    <>
      {canManageSpace(space) && shouldRenderContent(post) && (
        <div className="mb-4">
          <ModerationInfo.Regular record={post} contentType={POST_TYPE} />
        </div>
      )}
      <div
        className={classNames(
          "flex items-start justify-between gap-3 max-lg:mb-3",
          { "mb-5": isUsedInMinimalTipTapViewModal && isImagePost(post) },
        )}
      >
        <div className="flex grow flex-col items-start justify-between space-y-4 md:space-y-5">
          {post?.flagged_for_approval_at && (
            <Badge
              className="mt-1 uppercase"
              label={t("in_review")}
              variant="warning"
            />
          )}
          {shouldRenderMetaFirstAndThenTitle ? (
            <>
              <PostMetaInfo
                isRenderedOutsideTheSpace={isRenderedOutsideTheSpace}
                doesPostHaveCoverImage={doesPostHaveCoverImage}
                space={space}
                tagsLimit={tagsLimit}
                tagsShowMoreLabelVariant={tagsShowMoreLabelVariant}
                wrapperClassName={classNames("mt-1", metaInfoWrapperClassName)}
                hideAuthorHeadline={hideAuthorHeadline}
              />
              <PostName />
            </>
          ) : (
            <>
              <PostName />
              <PostMetaInfo
                isRenderedOutsideTheSpace={isRenderedOutsideTheSpace}
                doesPostHaveCoverImage={doesPostHaveCoverImage}
                space={space}
                tagsLimit={tagsLimit}
                tagsShowMoreLabelVariant={tagsShowMoreLabelVariant}
                wrapperClassName={classNames("mt-1", metaInfoWrapperClassName)}
                hideAuthorHeadline={hideAuthorHeadline}
              />
            </>
          )}
        </div>
        <PostActions
          post={post}
          onChange={onChange}
          refetchPosts={refetchPosts}
          onPostDestroy={onPostDestroy}
          hideShareLink={hideShareLink}
          hidePinnedToTopIcon={hidePinnedToTopIcon}
          hideBookmarkButton={hideBookmarkButton}
          hideSummarizeButton={hideSummarizeButton}
        />
      </div>
    </>
  );
};
